/**
 * MyInformationPage - User profile editing page.
 *
 * Allows users to view and edit their own profile information:
 * - First name
 * - Last name
 * - Email
 * - Phone
 *
 * Users cannot modify their username or any permission fields.
 * Optimized for mobile use by referees.
 */

import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import BackIcon from '@mui/icons-material/ArrowBack';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { authApi, type UserProfile, type UpdateProfileRequest } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';

export function MyInformationPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.is_staff ?? false;

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [telegram, setTelegram] = useState('');
  const [refereeYears, setRefereeYears] = useState(0);

  // Navigate back - admins go to previous page, non-admins go to judge
  const handleBack = () => {
    if (isAdmin) {
      navigate(-1);
    } else {
      navigate('/judge');
    }
  };

  // Fetch current profile
  const {
    data: profile,
    isLoading,
    error,
  } = useQuery<UserProfile>({
    queryKey: ['profile'],
    queryFn: () => authApi.getProfile(),
  });

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateProfileRequest) => authApi.updateProfile(data),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(['profile'], updatedProfile);
    },
  });

  // Initialize form when profile loads
  // This pattern is recommended by React docs for adjusting state based on props
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const initializedProfileRef = useRef<UserProfile | null>(null);
  // eslint-disable-next-line react-hooks/refs
  if (profile && profile !== initializedProfileRef.current) {
    // eslint-disable-next-line react-hooks/refs
    initializedProfileRef.current = profile;
    // Initialize form state from profile during render
    if (firstName !== (profile.first_name || '')) setFirstName(profile.first_name || '');
    if (lastName !== (profile.last_name || '')) setLastName(profile.last_name || '');
    if (email !== (profile.email || '')) setEmail(profile.email || '');
    if (phone !== (profile.phone || '')) setPhone(profile.phone || '');
    if (telegram !== (profile.telegram || '')) setTelegram(profile.telegram || '');
    if (refereeYears !== (profile.referee_years ?? 0)) setRefereeYears(profile.referee_years ?? 0);
  }

  // Track changes - use useMemo instead of useEffect to avoid cascading renders
  const hasChanges = useMemo(() => {
    if (!profile) return false;
    return (
      firstName !== (profile.first_name || '') ||
      lastName !== (profile.last_name || '') ||
      email !== (profile.email || '') ||
      phone !== (profile.phone || '') ||
      telegram !== (profile.telegram || '') ||
      refereeYears !== (profile.referee_years ?? 0)
    );
  }, [firstName, lastName, email, phone, telegram, refereeYears, profile]);

  const handleSave = () => {
    updateMutation.mutate({
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      telegram,
      referee_years: refereeYears,
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load profile information
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      <Button
        startIcon={<BackIcon />}
        onClick={handleBack}
        sx={{ mb: 2, minHeight: 44 }}
      >
        Takaisin
      </Button>

      <Typography variant="h4" fontWeight={700} gutterBottom>
        Omat tiedot
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Päivitä henkilötietosi. Muutokset tallentuvat kun painat Tallenna-nappia.
      </Typography>

      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Tietojen päivitys epäonnistui. Yritä uudelleen.
        </Alert>
      )}

      {updateMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Tiedot päivitetty!
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Username (read-only) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Käyttäjätunnus
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {profile?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Käyttäjätunnusta ei voi muuttaa
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Editable fields - touch-friendly sizing */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Etunimi"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            InputProps={{ sx: { minHeight: 56 } }}
          />

          <TextField
            label="Sukunimi"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            InputProps={{ sx: { minHeight: 56 } }}
          />

          <TextField
            label="Sähköposti"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            InputProps={{ sx: { minHeight: 56 } }}
          />

          <TextField
            label="Puhelinnumero"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            placeholder="esim. +358401234567"
            InputProps={{ sx: { minHeight: 56 } }}
          />

          <TextField
            label="Telegram"
            value={telegram}
            onChange={(e) => setTelegram(e.target.value)}
            fullWidth
            placeholder="käyttäjänimi ilman @-merkkiä"
            InputProps={{ sx: { minHeight: 56 } }}
          />

          <TextField
            label="Tuomarivuodet"
            type="number"
            value={refereeYears}
            onChange={(e) => setRefereeYears(Math.max(0, parseInt(e.target.value, 10) || 0))}
            fullWidth
            inputProps={{ min: 0, max: 99 }}
            InputProps={{ sx: { minHeight: 56 } }}
            helperText="Kuinka monta vuotta olet toiminut tuomarina"
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            sx={{ py: 1.5 }}
          >
            {updateMutation.isPending ? 'Tallennetaan...' : 'Tallenna'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

